/**
 * Avatars, small enough to live inside the league record.
 *
 * A phone camera photo is three or four megabytes; DynamoDB items stop at 400KB
 * and the whole league arrives in one request. So the picture is cropped square
 * and rescaled to 128px here, on the device, before it is ever uploaded — the
 * server only has to reject what slips through, not do the work.
 */

export const AVATAR_PX = 128;

/** Deterministic colour from a name, so the same person is the same colour. */
export function initialsColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  // Spread across the wheel but keep saturation and lightness in a band that
  // stays legible on the dark background.
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 42% 42%)`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export class AvatarError extends Error {}

export const GUILD_ICON_PX = 256;

/**
 * A custom deck's art, in the proportions Scryfall's art crops come in.
 *
 * Landscape rather than square, because this fills the same banner a real
 * commander's art does and a square would be cropped to a strip of itself on
 * the way in. Smaller than it is displayed on purpose: it rides inside the
 * league record, which every device fetches on every sync, and a slightly soft
 * banner is a much better trade than a document nobody can load on a phone.
 */
export const DECK_ART_W = 560;
export const DECK_ART_H = 400;

/** Any pixel that is not fully opaque means the image carries transparency. */
function hasTransparency(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  const { data } = ctx.getImageData(0, 0, w, h);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i]! < 255) return true;
  }
  return false;
}

/**
 * Encode the canvas as small as possible without throwing away transparency.
 *
 * JPEG has no alpha channel. Asking a canvas for one composites every
 * transparent pixel onto black first, so a logo cut out of its background comes
 * back welded to a black square — permanently, because the black is now real
 * pixels. That is only an acceptable trade for an image that was opaque anyway.
 *
 * So a transparent image goes PNG, then WebP, which keeps alpha and compresses
 * like a JPEG. Note that a browser without WebP support quietly returns a PNG
 * from toDataURL rather than failing, hence the prefix check.
 */
function encodeWithinBudget(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  maxBytes: number,
): string | null {
  const png = canvas.toDataURL('image/png');
  if (png.length <= maxBytes) return png;

  if (hasTransparency(ctx, w, h)) {
    for (const quality of [0.92, 0.85, 0.75, 0.65]) {
      const webp = canvas.toDataURL('image/webp', quality);
      if (webp.startsWith('data:image/webp') && webp.length <= maxBytes) return webp;
    }
    // No WebP and PNG is too big: shrinking loses detail, flattening loses the
    // cut-out. Detail is recoverable by uploading a better source; the cut-out
    // is not.
    return null;
  }

  for (const quality of [0.82, 0.7, 0.6]) {
    const jpeg = canvas.toDataURL('image/jpeg', quality);
    if (jpeg.length <= maxBytes) return jpeg;
  }
  return null;
}

/**
 * Read an image file and return a data URI at the given proportions.
 *
 * Centre-cropped rather than squashed: a portrait squeezed into a landscape
 * box is worse than one with its edges trimmed.
 */
async function resizeToDataUri(
  file: File,
  width: number,
  height: number,
  maxBytes: number,
): Promise<string> {
  if (!file.type.startsWith('image/')) throw new AvatarError('that is not an image');
  // Generous: it is about to be scaled down anyway, this only guards against
  // trying to decode something absurd on a phone.
  if (file.size > 25 * 1024 * 1024) throw new AvatarError('that image is too large');

  const bitmap = await createImageBitmap(file).catch(() => {
    throw new AvatarError('that image could not be read');
  });

  /*
   * The largest region of the source with the target's shape, centred.
   *
   * Whichever axis has proportionally more to give is the one trimmed, which
   * for a phone photo going into a banner means the top and bottom rather than
   * the sides — the same crop somebody would make by hand.
   */
  const want = width / height;
  const have = bitmap.width / bitmap.height;
  const cropW = have > want ? bitmap.height * want : bitmap.width;
  const cropH = have > want ? bitmap.height : bitmap.width / want;
  const sx = (bitmap.width - cropW) / 2;
  const sy = (bitmap.height - cropH) / 2;

  try {
    // Step down if the full size will not fit. Half the resolution beats no
    // image, and beats a flattened one.
    for (const scale of [1, 0.75, 0.5]) {
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new AvatarError('could not process that image');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, sx, sy, cropW, cropH, 0, 0, w, h);

      const encoded = encodeWithinBudget(canvas, ctx, w, h, maxBytes);
      if (encoded) return encoded;
    }
  } finally {
    bitmap.close();
  }
  throw new AvatarError('that image would not compress small enough — try a simpler one');
}

export const fileToAvatar = (file: File) =>
  resizeToDataUri(file, AVATAR_PX, AVATAR_PX, 48 * 1024);

export const fileToGuildIcon = (file: File) =>
  resizeToDataUri(file, GUILD_ICON_PX, GUILD_ICON_PX, 128 * 1024);

export const fileToDeckArt = (file: File) =>
  resizeToDataUri(file, DECK_ART_W, DECK_ART_H, 96 * 1024);
